import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import humps from 'lodash-humps-ts';
import { batch } from 'react-redux';
import { MenuStages } from '../constants/enums';
import { EventTypes } from '../constants/event';
import { Currency, Maybe } from '../generated-interfaces/graphql';
import {
  getSessionBufferMessages,
  IMessageReceived,
  messagingActions,
  resetHypothesisFrame,
  setIsAIActive,
} from '../reducers/messagingSlice';
import { AIStatusColors } from '../redux/features/ai/ai.constants';
import {
  resetAI,
  setAIStatus,
  setIsAIAutoMode,
  setIsAIEscalatedOnCarEnter,
  setIsAIEscalation,
} from '../redux/features/ai/ai.slice';
import {
  startLoading,
  stopLoading,
} from '../redux/features/loading/loading.slice';
import { RolePermissions } from '../types/restaurant';
import { get } from '../utils/api';
import { getFeatureFlagValues } from '../utils/network';
import { readEnvVariable } from '../utils';
import { update as updateConfig } from '../redux/features/config/config.slice';
import { resetGroupedUpsellDeclined } from '../utils/groupedUpsellDeclined';
import inMemoryGroupIdMapping from '../utils/inMemoryGroupIdMapping';
import { getAuthToken } from '../utils/local-storage';
import logger from '../utils/logger';
import {
  getRestaurantAccessLevels,
  getSelectedRestaurantAccess,
  hasRoleAccess,
  RestaurantAccess,
} from '../utils/restaurants';
import {
  fetchMenu,
  fetchMenuVersions,
  resetVersions,
  updateLoadedVersionDetails,
  updateMenuLoadingTimings,
  updateSelectedMenuVersion,
} from './menuSlice';
import { fetchGroupIdMapping, refreshAuthToken } from './userSlice';
import {
  getAuthRefactorFeatureFlag,
  getMenuUnificationFeatureFlag,
  setMenuUnificationFeatureFlag,
} from '../utils/session-storage';
import { updateNonCriticalCheckScore } from '../redux/features/healthScore/healthScore.slice';
import { NonCriticalChecksNames } from '../redux/features/healthScore/healthScore.constants';
import { setIsCurrentTaskUnsupervised } from '../redux/features/taskRouter/taskRouter.slice';
import { BILINGUAL_RESTAURANT_GROUP } from '../utils/constants';

export interface IRestaurantInfo {
  restaurantName: string;
  restaurantCode: string;
  restaurantBranding: Maybe<RestaurantBranding>;
}

export interface RestaurantState {
  selectedRestaurantDetails: {
    restaurantCode: string;
    primaryRestaurantCode: string;
    restaurantName: string;
    timezone: string;
    isUnsupervisedAi: boolean;
    isBilingual: boolean;
    restaurantSettings: {
      toGoModalityTaxRate: number;
      deliveryModalityTaxRate: number;
      dineInModalityTaxRate: number;
    };
  };

  restaurantsByUserRole: UserRestaurantsRole[];
  restaurantAccessLevels: RestaurantAccess;
  userRolesHaveLoaded: boolean;
  restaurantsById: Record<string, IRestaurantInfo>;
  selectedStage: MenuStages;
  isAIEnabled: Boolean;
}

export const initialState: RestaurantState = {
  selectedRestaurantDetails: {
    restaurantCode: '',
    primaryRestaurantCode: '',
    restaurantName: '',
    timezone: '',
    isUnsupervisedAi: false,
    isBilingual: false,
    restaurantSettings: {
      toGoModalityTaxRate: 0,
      deliveryModalityTaxRate: 0,
      dineInModalityTaxRate: 0,
    },
  },
  restaurantsByUserRole: [],
  restaurantAccessLevels: {},
  userRolesHaveLoaded: false,
  restaurantsById: {},
  selectedStage: MenuStages.LIVE,
  isAIEnabled: false, //Value indicating whether a restaurant is AI enabled (or) not
};

export type UserRestaurantsRole = {
  restaurants: Array<IRestaurantInfo>;
  role: RolePermissions;
};

export type HoursOfOperation = {
  availability?: Array<Availability>;
  timezone: string;
  currentUTCTime: string;
};

export type Availability = {
  alwaysEnabled: boolean;
  day: number;
  hours?: Array<Array<number>>;
};

export enum RestaurantAccountType {
  Demo = 'DEMO',
  Live = 'LIVE',
}

export type RestaurantBranding = {
  backgroundImageUrl: string;
  restaurantLogoUrl: string;
};

export type Restaurant = {
  accountType: RestaurantAccountType;
  hoursOfOperation: HoursOfOperation;
  primaryRestaurantCode: string;
  restaurantBranding: RestaurantBranding;
  restaurantCode: string;
  restaurantContactInfo: RestaurantContactInfo;
  restaurantName: string;
  restaurantSettings: RestaurantSettings;
};

export type RestaurantContactInfo = {
  address?: string;
  city?: string;
  country?: string;
  email?: string;
  phone_number?: string;
  state?: string;
  website?: string;
  zip_code?: string;
};

export type RestaurantSettings = {
  currency?: Currency;
  deliveryModalityTaxRate: number;
  dineInModalityTaxRate: number;
  disablePortalMenu?: boolean;
  isAlertingEnabled?: boolean;
  toGoDefaultSmsText?: string;
  toGoModalityTaxRate: number;
  toGoOrderConfirmationMessage?: string;
  toGoPickupMinutes?: number;
  isUnsupervisedAi?: boolean;
};

export const restaurantsByUserRole = createAsyncThunk(
  'restaurant/restaurantsByUserRole',
  async (_, { getState, rejectWithValue }) => {
    const {
      config: { RESTAURANT_SERVICE_URL },
    } = getState();

    const authToken = getAuthToken();
    if (authToken) {
      const response = await get({
        url: `${RESTAURANT_SERVICE_URL}/restaurants?data=roles`,
        headers: {
          Authorization: authToken,
        },
        errorCallback: (message) => {
          logger.error({
            message,
          });
        },
      });
      return humps(response?.data);
    } else if (getAuthRefactorFeatureFlag()) {
      logger.error({
        message:
          'AUTH error: No token found for calling restaurant service with data=roles',
      });
      rejectWithValue(
        'AUTH error: No token found for calling restaurant service with data=roles'
      );
    }
  }
);

export const selectStage = createAsyncThunk(
  'restaurant/selectStage',
  async ({ logout }: { logout: Function }, { getState, dispatch }) => {
    const {
      config: { FEATURE_FLAG_AUDIO_BACKEND },
      restaurant: {
        selectedRestaurantDetails: { timezone, restaurantCode },
        selectedStage,
      },
    } = getState();
    const { PLAYGROUND, LIVE } = MenuStages;
    const isPlayground = selectedStage === PLAYGROUND;
    const currentStage = isPlayground ? LIVE : PLAYGROUND;
    const currentMenuVersion = isPlayground ? '' : 'latest';

    dispatch(updateSelectedStage(currentStage));
    dispatch(updateSelectedMenuVersion(currentMenuVersion));

    if (restaurantCode) {
      dispatch(startLoading({}));
      dispatch(
        fetchMenu({
          currentMenuVersion,
          timezone,
          restaurantCode,
          currentStage,
          logout,
        })
      ).then((_) => {
        dispatch(messagingActions.clearMessages());
        dispatch(messagingActions.startConnecting());
        if (FEATURE_FLAG_AUDIO_BACKEND)
          dispatch(messagingActions.startAudioWSConnecting());
      });
    }
    return currentStage;
  }
);

const getRestaurantInfo = async ({
  dispatch,
  logout,
  restaurantCode,
  authToken,
  RESTAURANT_SERVICE_URL,
  currentTaskId,
}: {
  dispatch: Function;
  logout: Function;
  restaurantCode: string;
  authToken: string;
  RESTAURANT_SERVICE_URL: string;
  currentTaskId: string;
}) => {
  const infoPromise = get({
    url: `${RESTAURANT_SERVICE_URL}/restaurants/${restaurantCode}?data=info`,
    headers: {
      Authorization: authToken,
    },
    successCallback: (response) => {
      if (currentTaskId) {
        const isBilingual =
          response?.data?.restaurant_groups?.includes(
            BILINGUAL_RESTAURANT_GROUP
          ) || false;
        if (isBilingual) {
          dispatch(updateSelectedRestaurantBilingualStatus(isBilingual));
        }
      }
      dispatch(
        updateNonCriticalCheckScore({
          name: NonCriticalChecksNames.apiRestaurantInfo,
          value: 1,
        })
      );
    },
    errorCallback: (message) => {
      dispatch(stopLoading());
      logger.error({
        message,
        restaurantCode,
      });
      dispatch(
        updateNonCriticalCheckScore({
          name: NonCriticalChecksNames.apiRestaurantInfo,
          value: 0,
        })
      );
      if (getAuthRefactorFeatureFlag()) {
        logout('Error while fetching restaurant info data. Logging out');
      }
    },
  });

  if (!currentTaskId) {
    const response = (await infoPromise)?.data;
    return response;
  }
};

const resetStateForNewRestaurant = (
  dispatch: Function,
  {
    restaurantCode,
    featureFlagAudioBackend,
  }: {
    restaurantCode: string;
    featureFlagAudioBackend: boolean;
  }
) => {
  batch(() => {
    dispatch(messagingActions.closeConnection());
    dispatch(messagingActions.closeAudioWSConnection());
    dispatch(resetVersions());
    dispatch(updateLoadedVersionDetails(null));
    dispatch(updateSelectedRestaurantCode(restaurantCode));
    dispatch(updateSelectedMenuVersion(''));
    dispatch(updateSelectedStage(MenuStages.LIVE));
    dispatch(messagingActions.clearMessages());
    dispatch(messagingActions.startConnecting());
    dispatch(resetHypothesisFrame());
    // Clear per-restaurant Unleash flags so stale values from a previous
    // restaurant cannot leak through before the per-restaurant fetch resolves.
    dispatch(updateConfig({ FEATURE_FLAG_OCB_READBACK: false }));
    dispatch(updateAIEnabledStatus(false));
    dispatch(setIsAIActive(false));
    if (featureFlagAudioBackend) {
      dispatch(messagingActions.startAudioWSConnecting());
    }
  });
  resetGroupedUpsellDeclined();
};

interface RestaurantSelectionResult {
  timezone: string;
  restaurantName: string;
  primaryRestaurantCode: string;
  isUnsupervisedAi: boolean;
  restaurantSettings: {
    toGoModalityTaxRate: number;
    deliveryModalityTaxRate: number;
    dineInModalityTaxRate: number;
  };
  hasAIStatus: boolean;
}

/**
 * Extract restaurant data from task assignment — just enough to start fetchMenu.
 * Returns the result immediately; AI/session setup is deferred to completeTRSetup.
 */
const getTRRestaurantData = (
  tasksById: any,
  currentTaskId: string
): RestaurantSelectionResult => {
  const {
    restaurant_name,
    timezone: restaurantTimeZone,
    is_unsupervised_ai,
  } = tasksById[currentTaskId].data.restaurant_data || {};
  const currentTaskAIStatus = tasksById[currentTaskId].data?.ai_status;

  return {
    timezone: restaurantTimeZone,
    restaurantName: restaurant_name,
    primaryRestaurantCode: '',
    isUnsupervisedAi: is_unsupervised_ai,
    restaurantSettings: {
      toGoModalityTaxRate: 0,
      deliveryModalityTaxRate: 0,
      dineInModalityTaxRate: 0,
    },
    hasAIStatus: !!currentTaskAIStatus,
  };
};

/**
 * Non-critical TR setup: AI state, bilingual flag, session buffer messages.
 * Runs after fetchMenu has been dispatched so it doesn't compete for resources.
 */
const completeTRSetup = ({
  dispatch,
  tasksById,
  currentTaskId,
}: {
  dispatch: Function;
  tasksById: any;
  currentTaskId: string;
}) => {
  const { is_unsupervised_ai, ai_enabled = false } =
    tasksById[currentTaskId].data.restaurant_data || {};
  const currentTaskAIStatus = tasksById[currentTaskId].data?.ai_status;

  batch(() => {
    dispatch(updateSelectedRestaurantBilingualStatus(false));
    dispatch(updateAIEnabledStatus(ai_enabled));
    dispatch(setIsAIActive(ai_enabled));
    dispatch(setIsCurrentTaskUnsupervised(is_unsupervised_ai));

    if (currentTaskAIStatus) {
      dispatch(setIsAIAutoMode(true));
      dispatch(setAIStatus(currentTaskAIStatus));
      if (currentTaskAIStatus?.status === AIStatusColors.red) {
        dispatch(setIsAIEscalation(true));
      }
      if (currentTaskAIStatus?.escalate_on_car_enter) {
        dispatch(setIsAIEscalatedOnCarEnter(true));
      }
    }

    dispatch(
      getSessionBufferMessages({
        isCurrentTaskUnsupervised: !!currentTaskAIStatus,
      })
    );
  });
};

const handleNonTRRestaurantSelection = async ({
  dispatch,
  restaurantCode,
  logout,
  RESTAURANT_SERVICE_URL,
  FEATURE_FLAG_MENU_VERSION,
  restaurantAccessLevels,
  authToken,
}: {
  dispatch: Function;
  restaurantCode: string;
  logout: Function;
  RESTAURANT_SERVICE_URL: string;
  FEATURE_FLAG_MENU_VERSION: boolean;
  restaurantAccessLevels: RestaurantAccess;
  authToken: string;
}): Promise<RestaurantSelectionResult | null> => {
  const restaurantInfo = await getRestaurantInfo({
    dispatch,
    logout,
    restaurantCode,
    authToken,
    RESTAURANT_SERVICE_URL,
    currentTaskId: '',
  });
  const {
    restaurant_name,
    hours_of_operation,
    restaurant_settings,
    restaurant_groups,
  } = restaurantInfo || {};

  const isBilingual =
    restaurant_groups?.includes(BILINGUAL_RESTAURANT_GROUP) || false;
  dispatch(updateSelectedRestaurantBilingualStatus(isBilingual));

  const selectedRestaurantAccessLevel = getSelectedRestaurantAccess(
    restaurantCode,
    restaurantAccessLevels
  );
  const hasMenuVersionAccess = hasRoleAccess(
    RolePermissions.RestaurantManager,
    selectedRestaurantAccessLevel
  );

  logger.log({
    message: 'Refreshing auth token for the selected restaurant',
    restaurantCode,
  });

  await dispatch(refreshAuthToken({ logout }));

  if (FEATURE_FLAG_MENU_VERSION && hasMenuVersionAccess) {
    dispatch(
      fetchMenuVersions({
        restaurantCode,
        logout,
      })
    );
  }

  // Fire the per-restaurant Unleash fetch so the AI status bar is correct
  // before the first car enter; useSessionStatus refreshes on each new session.
  dispatch(fetchPerRestaurantUnleashFlags({ restaurantCode }));

  return {
    timezone: hours_of_operation?.timezone,
    restaurantName: restaurant_name,
    primaryRestaurantCode: '',
    isUnsupervisedAi: restaurant_settings?.is_unsupervised_ai,
    restaurantSettings: {
      toGoModalityTaxRate: restaurant_settings?.to_go_modality_tax_rate,
      deliveryModalityTaxRate: restaurant_settings?.delivery_modality_tax_rate,
      dineInModalityTaxRate: restaurant_settings?.dine_in_modality_tax_rate,
    },
    hasAIStatus: false,
  };
};

export const fetchPerRestaurantUnleashFlags = createAsyncThunk(
  'restaurant/fetchPerRestaurantUnleashFlags',
  async (
    { restaurantCode }: { restaurantCode: string },
    { dispatch, getState }
  ) => {
    const {
      taskRouter: { currentTaskId },
    } = getState();
    const isHITLMode = !currentTaskId;
    try {
      const flags = await getFeatureFlagValues({
        url: `${readEnvVariable('UNLEASH_API_URL')}?userId=${restaurantCode}`,
        names: ['ocb_available', 'ai_enabled'],
      });
      logger.log({
        message: 'per-restaurant Unleash flags resolved',
        moreInfo: { restaurantCode, flags, isHITLMode },
      });
      dispatch(
        updateConfig({ FEATURE_FLAG_OCB_READBACK: flags.ocb_available })
      );
      // TR mode sources ai_enabled from the task payload, so we only honor
      // the Unleash ai_enabled value in HITL mode.
      if (isHITLMode) {
        dispatch(updateAIEnabledStatus(flags.ai_enabled));
        dispatch(setIsAIActive(flags.ai_enabled));
      }
    } catch (error) {
      logger.error({
        message: 'Failed to fetch per-restaurant Unleash flags',
        moreInfo: { restaurantCode, error: String(error) },
      });
    }
  }
);

export const selectRestaurant = createAsyncThunk(
  'restaurant/selectRestaurant',
  async (
    {
      restaurantCode,
      logout,
      taskRouterMenuLoadFailureCallback,
      taskRouterMenuLoadSuccessCallback,
    }: {
      restaurantCode: string;
      logout: Function;
      taskRouterMenuLoadFailureCallback?: Function;
      taskRouterMenuLoadSuccessCallback?: Function;
    },
    { getState, dispatch, rejectWithValue }
  ) => {
    const {
      config: {
        FEATURE_FLAG_MENU_VERSION,
        RESTAURANT_SERVICE_URL,
        FEATURE_FLAG_AUDIO_BACKEND,
      },
      restaurant: { restaurantAccessLevels },
      taskRouter: { currentTaskId, tasksById },
    } = getState();

    // Step 1: Reset UI state (close WS, clear messages, reconnect)
    resetStateForNewRestaurant(dispatch, {
      restaurantCode,
      featureFlagAudioBackend: FEATURE_FLAG_AUDIO_BACKEND,
    });

    // Step 2: Get restaurant data (minimum work before fetchMenu)
    // - TR: extract from task assignment (synchronous, no dispatches yet)
    // - Non-TR: fetch restaurant info from API, refresh auth, load menu versions
    const authToken = getAuthToken();
    if (!authToken) {
      logger.error({
        message:
          'No auth token found for fetching restaurant service data=info API call',
        restaurantCode,
      });
      if (getAuthRefactorFeatureFlag()) {
        dispatch(stopLoading());
        logout(
          'No auth token found while fetching restaurant info data. Logging out'
        );
      }
      return null;
    }

    let result: RestaurantSelectionResult;

    if (currentTaskId) {
      result = getTRRestaurantData(tasksById, currentTaskId);
      //Need to get bilingual flag from restaurantInfo API but the call is Not blocking in TR mode
      getRestaurantInfo({
        dispatch,
        logout,
        restaurantCode,
        authToken,
        RESTAURANT_SERVICE_URL,
        currentTaskId,
      });
    } else {
      const nonTRResult = await handleNonTRRestaurantSelection({
        dispatch,
        restaurantCode,
        logout,
        RESTAURANT_SERVICE_URL,
        FEATURE_FLAG_MENU_VERSION,
        restaurantAccessLevels,
        authToken,
      });

      if (!nonTRResult) {
        return rejectWithValue(
          new Error(
            'No auth token found for fetching restaurant service data=info API call'
          )
        );
      }
      result = nonTRResult;
    }

    // Step 3: Fetch menu — start before non-critical setup to avoid CPU/bandwidth competition
    dispatch(
      fetchMenu({
        restaurantCode,
        timezone: result.timezone,
        currentMenuVersion: '',
        currentStage: MenuStages.LIVE,
        logout,
        ...(currentTaskId
          ? {
              taskRouterMenuLoadFailureCallback,
              taskRouterMenuLoadSuccessCallback,
            }
          : {}),
      })
    );

    // Step 4: Non-critical setup (runs after fetchMenu has been dispatched)
    // Load group ID mapping if not cached (PRP only — skip for unified menu)
    if (!getMenuUnificationFeatureFlag()) {
      const groupIdMappingsList =
        inMemoryGroupIdMapping.getGroupIdMappingList();
      if (!groupIdMappingsList[restaurantCode]) {
        dispatch(fetchGroupIdMapping(restaurantCode));
      }
    }

    // TR: configure AI state, bilingual flag, fetch session buffer messages
    if (currentTaskId) {
      completeTRSetup({ dispatch, tasksById, currentTaskId });
    }

    // Step 5: Reset AI state if no active AI session
    if (!result.hasAIStatus) {
      dispatch(resetAI());
    }

    // Step 6: Return payload (triggers .fulfilled for 6+ listening slices)
    return {
      restaurantCode,
      primaryRestaurantCode: result.primaryRestaurantCode,
      restaurantName: result.restaurantName,
      timezone: result.timezone,
      isUnsupervisedAi: result.isUnsupervisedAi,
      restaurantSettings: result.restaurantSettings,
    };
  }
);

export const restaurantState = createSlice({
  name: 'restaurant',
  initialState: initialState,
  reducers: {
    updateSelectedStage: (state, { payload }: PayloadAction<MenuStages>) => {
      state.selectedStage = payload;
    },
    resetSelectedRestaurantDetails: (state) => {
      state.selectedRestaurantDetails = {
        restaurantCode: '',
        primaryRestaurantCode: '',
        restaurantName: '',
        timezone: '',
        isUnsupervisedAi: false,
        isBilingual: false,
        restaurantSettings: {
          toGoModalityTaxRate: 0,
          deliveryModalityTaxRate: 0,
          dineInModalityTaxRate: 0,
        },
      };
      setMenuUnificationFeatureFlag('false');
    },
    updateSelectedRestaurantCode: (state, { payload }) => {
      state.selectedRestaurantDetails.restaurantCode = payload;
    },
    updateAIEnabledStatus: (state, { payload }: PayloadAction<boolean>) => {
      state.isAIEnabled = payload;
    },
    updateSelectedRestaurantBilingualStatus: (
      state,
      { payload }: PayloadAction<boolean>
    ) => {
      if (state.selectedRestaurantDetails) {
        state.selectedRestaurantDetails.isBilingual = payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(selectRestaurant.fulfilled, (state, action) => {
      state.selectedRestaurantDetails = {
        ...state.selectedRestaurantDetails,
        ...action.payload,
      };
    });
    builder.addCase(restaurantsByUserRole.fulfilled, (state, action) => {
      state.restaurantsByUserRole = action.payload;
      state.restaurantAccessLevels = getRestaurantAccessLevels(action.payload);
      state.userRolesHaveLoaded = true;
      const acc: Record<string, IRestaurantInfo> = {};
      state.restaurantsByUserRole.reduce((a, c) => {
        c.restaurants.forEach((r) => {
          if (!(r.restaurantCode in a)) {
            a[r.restaurantCode] = r;
          }
        });
        return a;
      }, acc);
      state.restaurantsById = acc;
    });
    builder.addCase(restaurantsByUserRole.rejected, (state, action) => {
      state.userRolesHaveLoaded = true;
      state.restaurantsById = {};
    });
    builder.addCase(selectStage.fulfilled, (state, action) => {
      state.selectedStage = action.payload;
    });
    builder.addCase(
      messagingActions.messageReceived,
      (state, action: PayloadAction<IMessageReceived>) => {
        const { messages } = action.payload;
        messages.forEach((message) => {
          if (message.event === EventTypes.aiStatus) {
            if (!state.isAIEnabled) state.isAIEnabled = true;
          }
        });
      }
    );
  },
});

export const {
  updateSelectedStage,
  resetSelectedRestaurantDetails,
  updateSelectedRestaurantCode,
  updateAIEnabledStatus,
  updateSelectedRestaurantBilingualStatus,
} = restaurantState.actions;

export default restaurantState.reducer;
